import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import { HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import {
  HD_CHART_GLOBAL_SETTINGS_STORAGE_KEY,
  loadHdChartGlobalSettings,
} from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-global-settings';
import {
  buildHdStreamsCheatSheet,
  HdStreamsCheatSheetGateFieldReader,
} from './hd-streams-cheat-sheet.builder';

/**
 * Pagina die alle 9 "stromen van bewustzijn" als cheat-sheet toont, nagebouwd uit
 * de Figma cheat sheet. Eén doorlopende pagina in de app; met de "splitsen voor
 * print"-toggle wordt het bij het printen opgedeeld in 2 bladen (5 + 4 streams)
 * zoals de originele Figma-export.
 *
 * Content komt uit de `human-design/streams` Transloco-scope (zie
 * {@link buildHdStreamsCheatSheet}); poort-/kanaal-keynotes + de extra info per
 * poort, de stream-namen en de legende staan daar als vertaalbare tekst. De
 * stream-visuals zijn geëxporteerde Figma-assets in `assets/human-design/streams/`.
 */
@Component({
  selector: 'hd-streams-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-streams-cheat-sheet-page.component.html',
  styleUrl: './hd-streams-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdStreamsCheatSheetPageComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly destroyRef = inject(DestroyRef);

  /** Splitst de pagina bij het printen in 2 bladen (default aan, zoals de Figma). */
  protected readonly splitForPrint = signal(true);

  /**
   * Spiegelt de globale chart-setting `includeNonHdFeatures` (in localStorage
   * via {@link HD_CHART_GLOBAL_SETTINGS_STORAGE_KEY}). Wanneer uit, verbergt de
   * cheat-sheet de spectrum-regels (dat is inhoud die niet tot de HD-canon
   * behoort). Cross-tab wijzigingen worden via het `storage`-event gevolgd.
   */
  protected readonly includeNonHdFeatures = signal(loadHdChartGlobalSettings().includeNonHdFeatures);

  protected readonly cheatSheet = computed(() => {
    const lang = this.contentLang.effectiveLang();
    return buildHdStreamsCheatSheet(this.reader(lang), this.gateFieldReader(lang));
  });

  protected readonly sheet1Rows = computed(() => this.cheatSheet().rows.filter(row => row.sheet === 1));
  protected readonly sheet2Rows = computed(() => this.cheatSheet().rows.filter(row => row.sheet === 2));

  constructor() {
    // Beide content-talen eager laden zodat de taalwissel én de NL-fallback voor
    // nog-niet-vertaalde EN-velden meteen correcte tekst opleveren.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');

    const storageListener = (event: StorageEvent) => {
      if (event.key !== HD_CHART_GLOBAL_SETTINGS_STORAGE_KEY) {
        return;
      }
      this.includeNonHdFeatures.set(loadHdChartGlobalSettings().includeNonHdFeatures);
    };
    window.addEventListener('storage', storageListener);
    this.destroyRef.onDestroy(() => window.removeEventListener('storage', storageListener));
  }

  /**
   * Reader voor de builder: leest een sleutel uit de `streams`-scope voor `lang`,
   * en valt terug op NL wanneer de waarde (nog) leeg is — zo toont de EN-weergave
   * de Nederlandse brontekst voor de nog niet vertaalde rijke velden.
   */
  private reader(lang: string): (key: string) => string {
    return (key: string) => {
      const value = this.gateInfo.streamCheatSheetTextIn(key, lang);
      if (value !== '') {
        return value;
      }
      return lang === 'nl' ? '' : this.gateInfo.streamCheatSheetTextIn(key, 'nl');
    };
  }

  /**
   * Reader voor de per-poort velden (streamFunction / fearKeynote) uit de
   * `gates`-scope, met dezelfde NL-fallback als {@link reader}.
   */
  private gateFieldReader(lang: string): HdStreamsCheatSheetGateFieldReader {
    return (gate, field) => {
      if (gate <= 0) {
        return '';
      }
      const value = this.gateInfo.gateInfoIn(gate, lang).translations[field];
      if (value !== '' || lang === 'nl') {
        return value;
      }
      return this.gateInfo.gateInfoIn(gate, 'nl').translations[field];
    };
  }

  protected toggleSplitForPrint(): void {
    this.splitForPrint.update(value => !value);
  }

  protected print(): void {
    window.print();
  }
}
